/**
 * OrdersPage.tsx
 * Family order history with filtering and status tracking
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrders } from '../hooks/useMarketplace';
import { OrderStatus, OrderFilters } from '../types/marketplaceTypes';

const ORDER_STATUS_CONFIG: Record<OrderStatus, { label: string; color: string; bg: string }> = {
  ['pending']: { label: 'Pending', color: 'text-[#8B8178]', bg: 'bg-[#8B8178]/10' },
  ['confirmed']: { label: 'Confirmed', color: 'text-[#6B8DD6]', bg: 'bg-[#6B8DD6]/10' },
  ['processing']: { label: 'Processing', color: 'text-[#C4785A]', bg: 'bg-[#C4785A]/10' },
  ['shipped']: { label: 'Shipped', color: 'text-[#7A9E7E]', bg: 'bg-[#7A9E7E]/10' },
  ['delivered']: { label: 'Delivered', color: 'text-[#5C7D60]', bg: 'bg-[#5C7D60]/10' },
  ['cancelled']: { label: 'Cancelled', color: 'text-[#C4785A]', bg: 'bg-[#C4785A]/10' },
  ['refunded']: { label: 'Refunded', color: 'text-[#8B8178]', bg: 'bg-[#8B8178]/10' },
};

// This would come from auth context in real app
const FAMILY_ID = 'current-family-id';

export const OrdersPage: React.FC = () => {
  const [filters, setFilters] = useState<OrderFilters>({
    page: 1,
    limit: 10,
  });
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');

  const { data: ordersData, isLoading, error } = useOrders(FAMILY_ID, filters);

  const statusOptions: Array<{ value: OrderStatus | 'all'; label: string }> = [
    { value: 'all', label: 'All Orders' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  const filteredOrders = ordersData?.items.filter(
    order => statusFilter === 'all' || order.status === statusFilter
  ) || [];

  return (
    <div className="min-h-screen bg-[#FDF8F3] py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-[#2B3A4D] font-['Fraunces']">
              Order History
            </h1>
            <p className="text-[#8B8178] mt-1">
              Track and manage your marketplace orders
            </p>
          </div>
          <Link
            to="/marketplace"
            className="px-4 py-2 text-[#7A9E7E] font-medium hover:bg-[#7A9E7E]/10 rounded-lg transition-colors"
          >
            Continue Shopping
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-medium text-[#2B3A4D]">Filter by status:</span>
            <div className="flex flex-wrap gap-2">
              {statusOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === option.value
                      ? 'bg-[#7A9E7E] text-white'
                      : 'bg-[#F5EDE4] text-[#2B3A4D] hover:bg-[#E5E0DB]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Orders List */}
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="flex justify-between mb-4">
                  <div className="h-5 w-32 bg-[#F5EDE4] rounded" />
                  <div className="h-5 w-24 bg-[#F5EDE4] rounded" />
                </div>
                <div className="h-4 w-48 bg-[#F5EDE4] rounded mb-2" />
                <div className="h-4 w-36 bg-[#F5EDE4] rounded" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <p className="text-[#C4785A] mb-4">Error loading orders</p>
            <button
              onClick={() => window.location.reload()}
              className="text-[#7A9E7E] font-medium hover:text-[#5C7D60]"
            >
              Try Again
            </button>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <svg className="w-16 h-16 mx-auto text-[#E5E0DB] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            <h3 className="text-lg font-semibold text-[#2B3A4D] mb-2">No orders found</h3>
            <p className="text-[#8B8178] mb-6">
              {statusFilter === 'all' 
                ? "You haven't placed any orders yet."
                : `No orders with status "${statusOptions.find(o => o.value === statusFilter)?.label}"`}
            </p>
            <Link
              to="/marketplace"
              className="inline-flex items-center px-6 py-3 bg-[#7A9E7E] text-white rounded-xl font-medium hover:bg-[#5C7D60] transition-colors"
            >
              Browse Marketplace
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const statusConfig = ORDER_STATUS_CONFIG[order.status];
              
              return (
                <Link
                  key={order.id}
                  to={`/family/orders/${order.id}`}
                  className="block bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-[#8B8178]">Order #{order.order_number}</p>
                      <p className="text-sm text-[#8B8178]">
                        {new Date(order.ordered_at || order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusConfig.color} ${statusConfig.bg}`}>
                      {statusConfig.label}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mb-4">
                    {/* Product placeholder - in real app would show actual product image */}
                    <div className="w-16 h-16 rounded-lg bg-[#F5EDE4] flex items-center justify-center flex-shrink-0">
                      <svg className="w-8 h-8 text-[#8B8178]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#2B3A4D] truncate">{order.description || 'Order'}</p>
                      <p className="text-sm text-[#8B8178]">
                        Qty: {order.quantity} • Vendor: {order.vendor_id}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#2B3A4D]">
                        ${order.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>

                  {/* Tracking Info */}
                  {order.tracking_number && order.status === 'shipped' && (
                    <div className="flex items-center gap-2 text-sm text-[#7A9E7E] bg-[#7A9E7E]/5 px-4 py-2 rounded-lg">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Tracking: {order.tracking_number}</span>
                    </div>
                  )}

                  {/* View Details Arrow */}
                  <div className="flex items-center justify-end mt-4 text-[#7A9E7E]">
                    <span className="text-sm font-medium">View Details</span>
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              );
            })}

            {/* Pagination */}
            {ordersData && ordersData.total_pages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                {[...Array(ordersData.total_pages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setFilters(prev => ({ ...prev, page: i + 1 }))}
                    className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                      filters.page === i + 1
                        ? 'bg-[#7A9E7E] text-white'
                        : 'bg-white text-[#2B3A4D] hover:bg-[#F5EDE4]'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;


